import { useEffect, useState } from "react";
import "./NotificationsView.css";
import Form from "../../../../components/form/Form";
import Icon from "../../../../components/base/Icon";
import ToggleSwitch from "../../../../components/form/ToggleSwitch";
import { toastSuccess } from "../../../../components/base/Toast";
import { getUser, setUser, useHub, useScreen } from "../../../../core/Global";
import { useNotificationPermission } from "../../../../hooks/useNotificationPermission";
import User from "../../../accounts/models/User";
import {
    NotificationKey,
    NotificationOptionKey,
    NotificationOptions,
    NotificationSection,
    NotificationSections,
    NotificationTypeSettings,
    NotificationsSettingsModel,
    defaultNotificationsSettings,
    normalizeNotificationsSettings
} from "../models/NotificationsSettings";


interface IProps {
    // Set when an admin opens someone else's settings from the users list. Without it
    // the page edits the signed-in user, which is the normal case.
    target?: User
    onSaved?: (user: User) => void
}


export default function NotificationsView(props: IProps = {}) {

    const hub = useHub()
    const screen = useScreen()
    const perm = useNotificationPermission()
    const [working, workingSet] = useState<boolean>(false)
    const [model, modelSet] = useState<NotificationsSettingsModel>(defaultNotificationsSettings)

    useEffect(() => {
        const target = props.target ?? getUser()
        if (target) modelSet(normalizeNotificationsSettings(target.notificationsSettings))
    }, [props.target, getUser()])

    const user = props.target ?? getUser()

    // Someone else's settings: the browser permission of this machine says nothing
    // about whether notifications reach them, so it is not their business here.
    const editingSelf = !props.target || props.target.id == getUser()?.id


    const update = (key: NotificationKey, patch: Partial<NotificationTypeSettings>) => {
        modelSet(prev => {
            const next = { ...prev }
            next[key] = { ...prev[key], ...patch }
            return next
        })
    }

    const toggleOption = (key: NotificationKey, option: NotificationOptionKey) => {
        modelSet(prev => {
            const next = { ...prev }
            const value: NotificationTypeSettings = { ...prev[key] }
            value[option] = !value[option]

            // Whatever depended on this option has nothing left to act on, so it goes
            // off with it rather than lingering as a setting nobody can see.
            if (!value[option]) {
                NotificationOptions
                    .filter(f => f.requires == option)
                    .forEach(f => value[f.key] = false)
            }

            next[key] = value
            return next
        })
    }


    const renderSection = (section: NotificationSection) => {

        const value = model[section.key]

        return (
            <div key={`notif-${section.key}`} className="notif-section">

                {/* The whole row toggles, the switch itself is click-through (see CSS). */}
                <div className="notif-row"
                    onClick={() => update(section.key, { enabled: !value.enabled })}>
                    <div className="notif-row-icon" style={{ backgroundColor: section.color }}>
                        <Icon icon={section.icon} type="solid" size={15} />
                    </div>
                    <div className="notif-row-body">
                        <div className="notif-row-text">
                            <div className="notif-row-title">{section.name}</div>
                            <div className="notif-row-desc">{section.description}</div>
                        </div>
                        <ToggleSwitch raw small className="notif-row-switch"
                            checked={value.enabled}
                            onChange={e => update(section.key, { enabled: e })} />
                    </div>
                </div>

                {
                    value.enabled &&
                    <div className="notif-options">
                        {
                            NotificationOptions
                                .filter(option => !option.requires || value[option.requires])
                                .map(option =>
                                <div key={`notif-${section.key}-${option.key}`}
                                    className="notif-row notif-row-option"
                                    onClick={() => toggleOption(section.key, option.key)}>
                                    <div className={`notif-option-icon${value[option.key] ? " notif-option-icon-on" : ""}`}>
                                        <Icon icon={option.icon} type={value[option.key] ? "solid" : "regular"} size={15} />
                                    </div>
                                    <div className="notif-row-body">
                                        <div className="notif-row-text">
                                            <div className="notif-row-title">{option.name}</div>
                                            <div className="notif-row-desc">{option.hint}</div>
                                        </div>
                                        <ToggleSwitch raw small className="notif-row-switch"
                                            checked={value[option.key]}
                                            onChange={() => toggleOption(section.key, option.key)} />
                                    </div>
                                </div>
                            )
                        }
                    </div>
                }

            </div>
        )
    }


    return (
        <Form className="form notif-settings"
            onSubmit={(body, callback) => {
                workingSet(true)

                const url = editingSelf
                    ? "/accounts/profile/notificationssettings"
                    : "/accounts/users/notificationssettings/" + props.target?.id

                hub.Post(url, model, res => {
                    if (res.success) {
                        // The server hands the updated user back. For your own settings
                        // that refreshes everything reading getUser() (the notification
                        // center included); for someone else's it goes to the caller.
                        if (res.data) {
                            if (editingSelf) setUser(res.data)
                            props.onSaved?.(res.data)
                        }
                        toastSuccess("Notification settings has been updated successfully!")
                    }
                    if (callback) callback(res)
                    workingSet(false)
                })
            }}>
            <div className="card mb-5">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">
                            Notifications
                        </h3>
                    </div>
                    {
                        /* On mobile the save button lives at the bottom of the page instead,
                           where it stays reachable as more notification types are added. */
                        !screen.isMobile &&
                        <div className="card-action">
                            <button type="submit" className="btn btn-sm btn-primary" disabled={working}>
                                {
                                    working ?
                                        <>
                                            <Icon icon="spinner-third" spin />
                                            <span className="ms-2">Updating...</span>
                                        </>
                                        : <>Update Settings</>
                                }
                            </button>
                        </div>
                    }
                </div>
                <div className="card-body border-top notif-body">

                    {
                        /* Nothing below works without the browser's permission, so it is
                           said before the settings rather than after they fail silently. */
                        editingSelf && !perm.granted &&
                        <div className="notif-warning">
                            <div className="notif-warning-icon">
                                <Icon icon="triangle-exclamation" type="solid" size={15} />
                            </div>
                            <div className="notif-warning-body">
                                <div className="notif-warning-title">
                                    {
                                        !perm.supported ? "This browser cannot show notifications"
                                            : perm.blocked ? "Notifications are blocked in this browser"
                                                : "Notifications are not turned on in this browser"
                                    }
                                </div>
                                <div className="notif-warning-text">
                                    {
                                        !perm.supported ?
                                            <>Nothing below can reach you here. Open Rinvox in a different browser to be notified.</>
                                            : perm.blocked ?
                                                <>Only you can undo this, from the browser itself: open the site settings from the icon on the left of the address bar and set <b>Notifications</b> to <b>Allow</b>.</>
                                                : perm.askedInBrowser ?
                                                    <>Your browser is asking up in the address bar — open the icon there and choose <b>Allow</b>.</>
                                                    : <>Until you allow them, nothing below can reach you on this device.</>
                                    }
                                </div>
                            </div>
                            {
                                perm.supported && !perm.blocked &&
                                <button type="button" className="btn btn-sm btn-warning notif-warning-action"
                                    disabled={perm.working} onClick={perm.ask}>
                                    {
                                        perm.working ?
                                            <>
                                                <Icon icon="spinner-third" spin />
                                                <span className="ms-2">Waiting...</span>
                                            </>
                                            : <>Allow Notifications</>
                                    }
                                </button>
                            }
                        </div>
                    }

                    <div className="notif-note">
                        <Icon icon="info-circle" className="text-muted me-2" />
                        <span>
                            {
                                editingSelf ?
                                    <>These settings are yours alone and follow you to every device you sign in on.</>
                                    : <>You are editing <b>{user?.fullName}</b>'s own notification settings.</>
                            }
                        </span>
                    </div>

                    <div className="notif-note notif-note-legend">
                        <span>
                            <b>Push</b> is delivered by the device (browser or mobile app) even when the
                            app is closed, <b>In-App</b> only shows up while the app is open.
                        </span>
                    </div>

                    {NotificationSections.map(renderSection)}

                    {
                        screen.isMobile &&
                        <button type="submit" className="btn btn-primary w-100 mt-5" disabled={working}>
                            {
                                working ?
                                    <>
                                        <Icon icon="spinner-third" spin />
                                        <span className="ms-2">Updating...</span>
                                    </>
                                    : <>Update Settings</>
                            }
                        </button>
                    }

                </div>
            </div>
        </Form>
    )
}
